import { ProviderEdit } from './../model/provider-edit';
import { Component, OnInit, ChangeDetectorRef, AfterViewChecked, ViewChild } from '@angular/core';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { State, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { ProviderListService } from 'src/app/core/service/grid-data/grid-data-provider.service';
import { GridDataResult, DataStateChangeEvent, CellClickEvent, GridComponent } from '@progress/kendo-angular-grid';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { Papa } from 'ngx-papaparse';
import moment from 'moment';
import { saveAs } from '@progress/kendo-file-saver';
import { ProviderImportComponent } from '../provider-import/provider-import.component';
import copyToClipboard from 'copy-to-clipboard';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { ProviderService } from 'src/app/core/service/provider.service';
import { Dictionary } from 'src/app/core/model/dictionary';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';

@Component({
  standalone: false,
  selector: 'app-provider-list',
  templateUrl: './provider-list.component.html',
  styleUrls: ['./provider-list.component.scss']
})
export class ProviderListComponent implements OnInit, AfterViewChecked
{
  @ViewChild(GridComponent) public grid: GridComponent;
  @ViewChild('importProviders') public importProviders: ProviderImportComponent;

  public pageSizes = [5, 10, 25, 50, 100];
  public deleteDialogOpened = false;
  public enableAdvanced = false;
  public enableAutoExpand = false;
  public gridView: GridDataResult;
  public gridData: any = {};
  public count: number;
  public genders = [];
  public editDataItem: ProviderEdit;
  public selectedProvider: ProviderEdit;
  public providerInUse: boolean;
  public isDirectoryManager = false;

  /**
   * Provider list Kendo grid state.
   */
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    },
    sort: [
      { field: 'lastName', dir: 'asc' }
    ]
  };

  public filterText = '';
  public phoneNumberMask: string;
  public isNew = false;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private searchHelperService: SearchHelperService,
    private providerListService: ProviderListService,
    private appConfigService: AppConfigService,
    private settingService: SettingService,
    private dictionaryService: DictionaryService,
    private papa: Papa,
    private messageHandlerService: MessageHandlerService,
    private providerService: ProviderService,
    private authService: AuthenticationService
  )
  {
    this.state.take = this.appConfigService.getConfig().pageSize;
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.phoneNumberMask = sessionSetting.phoneMask;
        this.isDirectoryManager = (this.authService.currentUser.role === 'ROLE_DIRECTORY_MANAGER');
      }
    });
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.providerListService ? this.providerListService.loading : false;
  }

  public ngOnInit(): void
  {
    this.getGenderCodes();
    this.providerListService.subscribe({
      next: (value) =>
      {
        if (value !== null)
        {
          value.data.forEach(d =>
          {
            d.createdOn = new Date(d.createdOn);
            if (d.lastModifiedOn !== null)
            {
              d.lastModifiedOn = new Date(d.lastModifiedOn);
            }
          });
        }

        // TODO - this interaction between the component and the data service can be simplified / clean up.
        this.gridView = {
          data: (value) ? this.gridData = value.data : this.gridData = [],
          total: (value) ? this.count = value.total : this.count = 0
        };
      }
    });

    this.getProviders();
  }

  public ngAfterViewChecked(): void
  {
    this.changeDetectorRef.detectChanges();
  }

  public searchFieldChange(filterText: string): void
  {
    this.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.state, 'fullName');
    this.filterText = filterText;
    this.filterChange(root);
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.providerListService.queryByState(state);
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.state.filter = filter;
    this.providerListService.queryByState(this.state);
  }

  /**
   * Indicates if the grid details visibility. Details will show only if the provider have an address.
   *
   * @param dataItem Grid dataItem
   * @param index Index of the data in the grid list.
   */
  public showDetailsOnlyIfAddress(dataItem: any): boolean
  {
    return dataItem.practiceAddress &&
      ((dataItem.practiceAddress.streetLine !== '' && dataItem.practiceAddress.streetLine !== null) ||
        (dataItem.practiceAddress.streetLineAdditional !== '' && dataItem.practiceAddress.streetLineAdditional !== null) ||
        (dataItem.practiceAddress.city !== '' && dataItem.practiceAddress.city !== null) ||
        (dataItem.practiceAddress.postalCode !== '' && dataItem.practiceAddress.postalCode !== null) ||
        (dataItem.practiceAddress.state !== null) ||
        (dataItem.practiceAddress.country !== null));
  }

  /**
   * Event fires when a task is selected.
   */
  public onCellClick = (event: CellClickEvent): void =>
  {
    this.collapseAllRows();
    if (this.enableAutoExpand)
    {
      event.sender.expandRow(event.rowIndex);
    }
  };

  /**
   * Export the selected code set to a CSV file, and trigger a browser download.
   */
  public exportProviderToCsv(): void
  {
    const state: State = {
      skip: 0,
      take: 99999,
      filter: {
        logic: 'and',
        filters: []
      }
    };

    this.providerListService.queryByState(state, (providers) =>
    {
      if (providers && providers.total > 0)
      {
        const data = providers.data.map(p => ({
          id: p.id,
          firstName: p.firstName,
          lastName: p.lastName,
          middleName: p.middleName,
          gender: p.gender,
          professionalSuffix: p.professionalSuffix,
          suffix: p.suffix,
          title: p.title,
          email: p.email,
          directAddress: p.directAddress,
          organization: p.organization,
          specialty: p.specialty,
          specialtyName: p.specialtyName,
          npi: p.npi,
          telephoneNumber: p.telephoneNumber,
          telephoneNumberExtension: p.telephoneNumberExtension,
          cellPhoneNumber: p.cellPhoneNumber,
          faxNumber: p.faxNumber,
          addressLine1: p.practiceAddress ? p.practiceAddress.streetLine : '',
          addressLine2: p.practiceAddress ? p.practiceAddress.streetLineAdditional : '',
          city: p.practiceAddress ? p.practiceAddress.city : '',
          county: p.practiceAddress ? p.practiceAddress.county : '',
          state: p.practiceAddress ? p.practiceAddress.state : '',
          postalCode: p.practiceAddress ? p.practiceAddress.postalCode : '',
          country: p.practiceAddress ? p.practiceAddress.country : '',
          latitude: p.practiceAddress ? p.practiceAddress.latitude : '',
          longitude: p.practiceAddress ? p.practiceAddress.longitude : '',
          qualityScore: p.qualityScore,
          languages: this.getLanguageCodes(p.languages),
          url: p.url ? p.url : '',
          description: p.description
        }));

        const csvContent = this.papa.unparse(data);

        // set the default file name
        const now = moment().format('YYYYMMDD_hhmmss');
        const csvFileName = `Providers-${now}.csv`;

        // create a Blob object
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

        const url = URL.createObjectURL(blob);
        saveAs(url, csvFileName);

        // Reload provides
        this.getProviders();
      }
    });
  }

  public openProvidersImport(): void
  {
    this.importProviders.execute();
  }

  public onLoadedProviders(): void
  {
    this.getProviders();
  }

  /**
   * Provide the full provider name to display.
   *
   * @param dataItem Current row dataItem.
   */
  public getProviderName(dataItem: any): string
  {
    return (dataItem !== undefined && dataItem !== null) ? `${dataItem.firstName} ${dataItem.lastName}` : '';
  }

  /**
   * Copy the direct address to clipboard.
   *
   * @param directAddress Direct address to copy.
   */
  public copyDirectAddress(directAddress: string): void
  {
    copyToClipboard(directAddress);
  }

  /**
   * Add provider handler
   */
  public addHandler(): void
  {
    this.isNew = true;
    setTimeout(() =>
    {
      this.editDataItem = new ProviderEdit();
    });
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Save provider handler.
   *
   * @param provider Provider object to save.
   */
  public saveChangesHandler(provider: ProviderEdit): void
  {
    this.providerService.saveProvider(provider, this.isNew, provider.id).subscribe({
      next: () =>
      {
        this.editDataItem = undefined;
        this.messageHandlerService.displaySuccess('PROVIDER-SAVED-SUCCESSFULLY');
        this.getProviders();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Cancel provider changes.
   */
  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  /**
   * Edit handler
   *
   * @param id Provider id.
   */
  public editHandler(id: number): void
  {
    this.isNew = false;
    this.providerService.getProviderById(id).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number, npi: string): void
  {
    // Check if in use
    this.providerService.existsProvider(npi).subscribe({
      next: (inUseResponse) =>
      {
        this.providerInUse = inUseResponse;
        this.providerService.getProviderById(id).subscribe({
          next: (data) =>
          {
            this.selectedProvider = data as ProviderEdit;
            this.open('deleteDialog');
          },
          error: (errorResponse) =>
          {
            this.messageHandlerService.displayError(errorResponse);
          }
        });
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      const providerId = this.selectedProvider.id;
      this.providerService.deleteProvider(providerId).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('PROVIDER-DELETED-SUCCESSFULLY');
          this.getProviders();
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    // post-deletion cleanup and refresh grid
    this.close('deleteDialog');
    this.selectedProvider = null;
  }

  public getLanguageCodes(languages: Dictionary[]): string
  {
    let languageCodes = '';
    if (languages !== undefined && languages !== null)
    {
      languages.forEach(o =>
      {
        languageCodes += o.code + '|';
      });
      languageCodes = languageCodes.slice(0, -1);
    }
    return languageCodes;
  }

  /**
   * Get providers from API per the applied filter.
   *
   * @param filter Task filter string.
   */
  private getProviders(): void
  {
    this.state.skip = 0;
    this.providerListService.queryByState(this.state);
  }

  /**
   * Get the list of genders from distionary database.
   */
  private getGenderCodes(): void
  {
    this.dictionaryService.getCachedGenders().subscribe({
      next: (data) =>
      {
        this.genders = data;
      }
    });
  }

  /**
   * Collapse all task details opened.
   */
  private collapseAllRows()
  {
    if (this.grid === undefined)
    {
      return;
    }
    // Will collapse all preceeding task details based on the page size and current page.
    const maxLength = this.appConfigService.getConfig().pageSize + this.state.skip;
    for (let i = 0; i < maxLength; i++)
    {
      this.grid.collapseRow(i);
    }
  }
}
